import {
  Alert,
  Link,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import React from 'react';
import { toTitleCase } from '../../lib/toTitleCase';
import { Agartyp, InnehavDto } from '../../model/ipm-innehav';
import { ForetagTable } from '../ForetagTable/ForetagTable';
import styles from './InnehavInfoSearchResult.module.css';

const agartypMap: { [key in Agartyp]: string } = {
  LAGFAREN_AGARE: 'Lagfaren ägare',
  TOMTRATTSINNEHAVARE: 'Tomträttsinnehavare',
  TAXERAD_AGARE: 'Taxerad ägare',
};

const formatAgartyps = (agartyps: string[]): string =>
  agartyps
    .map((typ, i) =>
      i ? agartypMap[typ].toLocaleLowerCase() : agartypMap[typ]
    )
    .join(', ');

const formatFastighetSokUrl = (fastighetsbeteckning: string): string =>
  '/fsok/SokFastighet.seam?fastighet=' + fastighetsbeteckning;

interface ForetagInnehavResultProps {
  innehav: InnehavDto[];
}

export const InnehavInfoSearchResult: React.FC<ForetagInnehavResultProps> = ({
  innehav = [],
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const paginatedInnehav =
    rowsPerPage > 0
      ? innehav.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : innehav;

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div>
      <div className={styles.printWrapper}>
        {innehav.length ? (
          <ForetagTable>
            <TableHead>
              <TableRow>
                <TableCell>Beteckning</TableCell>
                <TableCell>Registerenhetens UUID</TableCell>
                <TableCell>Ägartyp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {innehav?.map((innehav) => (
                <TableRow key={`fastighetref_${innehav.uuid}`}>
                  <TableCell>
                    {toTitleCase(innehav.fastighetsbeteckning)}
                  </TableCell>
                  <TableCell>{innehav.uuid}</TableCell>
                  <TableCell>{formatAgartyps(innehav.agartyper)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ForetagTable>
        ) : (
          <Alert severity="info">
            Inga fastigheter funna för organisationen
          </Alert>
        )}
      </div>
      <div className="hideInPrint">
        {innehav.length ? (
          <ForetagTable className={styles.innehavTable}>
            <TableHead>
              <TableRow>
                <TableCell>Beteckning</TableCell>
                <TableCell>Registerenhetens UUID</TableCell>
                <TableCell>Ägartyp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInnehav?.map((innehav) => (
                <TableRow key={`fastighetref_${innehav.uuid}`}>
                  <TableCell>
                    <Link
                      href={formatFastighetSokUrl(innehav.fastighetsbeteckning)}
                      className={styles.fastighetLink}
                    >
                      {toTitleCase(innehav.fastighetsbeteckning)}
                    </Link>
                  </TableCell>
                  <TableCell>{innehav.uuid}</TableCell>
                  <TableCell>{formatAgartyps(innehav.agartyper)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter sx={{ borderTop: '1px solid #DDD' }}>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'Alla', value: -1 }]}
                  colSpan={3}
                  count={innehav.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'Rader per sida',
                    },
                    native: true,
                  }}
                  onPageChange={(_e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </ForetagTable>
        ) : (
          <Alert severity="info">
            Inga fastigheter funna för organisationen
          </Alert>
        )}
      </div>
    </div>
  );
};

